import React, { useState, useMemo } from 'react';
import './DateGen.css';

interface DateIdea {
  activity: string;
  consumable: string;
}

interface DateOption {
  name: string;
  romanceLevel: number;
}

interface ItineraryLocation {
  name: string;
  address: string;
  time: string;
}

const activities: DateOption[] = [
  // Cozy/Active (1-2)
  { name: 'Morning run together', romanceLevel: 1 },
  { name: 'Rock Creek trail jog', romanceLevel: 1 },
  { name: 'Yoga at home', romanceLevel: 2 },
  { name: 'Cook breakfast together', romanceLevel: 2 },
  { name: 'Board games & puzzles', romanceLevel: 2 },

  // Festive (3)
  { name: 'ZooLights walk', romanceLevel: 3 },
  { name: 'National Mall stroll', romanceLevel: 3 },
  { name: 'Georgetown window shopping', romanceLevel: 3 },
  { name: 'Holiday movie marathon', romanceLevel: 3 },
  { name: 'CityCenterDC lights', romanceLevel: 3 },

  // Romantic (4)
  { name: 'Ice skating at Georgetown', romanceLevel: 4 },
  { name: 'Wine & cheese evening', romanceLevel: 4 },
  { name: 'Candlelit cooking', romanceLevel: 4 },
  { name: 'Sculpture Garden skating', romanceLevel: 4 },
  { name: 'Fireplace & reading', romanceLevel: 4 },

  // Very Romantic (5)
  { name: 'Couples massage', romanceLevel: 5 },
  { name: 'Fancy dinner out', romanceLevel: 5 },
  { name: 'Champagne & stargazing', romanceLevel: 5 },
  { name: 'Slow morning in bed', romanceLevel: 5 },
];

const consumables: DateOption[] = [
  // Light (1-2)
  { name: 'Matcha lattes', romanceLevel: 1 },
  { name: 'Fresh juice', romanceLevel: 1 },
  { name: 'Homemade granola', romanceLevel: 2 },
  { name: 'Hot chocolate', romanceLevel: 2 },
  { name: 'Cinnamon rolls', romanceLevel: 2 },

  // Festive (3)
  { name: 'Mulled wine', romanceLevel: 3 },
  { name: 'Peking duck', romanceLevel: 3 },
  { name: 'Eggnog', romanceLevel: 3 },
  { name: 'Holiday cookies', romanceLevel: 3 },
  { name: 'Tru Fru berries', romanceLevel: 3 },

  // Elevated (4)
  { name: 'Natural wine', romanceLevel: 4 },
  { name: 'Feast of Seven Fishes', romanceLevel: 4 },
  { name: 'Fresh oysters', romanceLevel: 4 },
  { name: 'Artisanal cheese board', romanceLevel: 4 },
  { name: 'Craft cocktails', romanceLevel: 4 },

  // Indulgent (5)
  { name: 'Champagne', romanceLevel: 5 },
  { name: 'Caviar & blinis', romanceLevel: 5 },
  { name: 'Bûche de Noël', romanceLevel: 5 },
  { name: 'Prime rib feast', romanceLevel: 5 },
];

const DateGen: React.FC = () => {
  const [currentDate, setCurrentDate] = useState<DateIdea | null>(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [history, setHistory] = useState<DateIdea[]>([]);
  const [romanceLevel, setRomanceLevel] = useState(4);
  const [jennsPick, setJennsPick] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'christmas-eve' | 'christmas-day'>('christmas-eve');

  const getItineraryData = (pick: string): ItineraryLocation[] => {
    const itineraries: Record<string, ItineraryLocation[]> = {
      // Christmas Eve options
      'jaleo': [
        { name: 'Penn Quarter Walk', address: 'Penn Quarter, Washington, DC', time: '5:00pm' },
        { name: 'Jaleo', address: '480 7th St NW, Washington, DC', time: '7:00pm' },
        { name: 'CityCenterDC Lights', address: 'CityCenterDC, Washington, DC', time: '9:30pm' }
      ],
      'cozy-cooking': [
        { name: 'Whole Foods', address: '2201 I St NW, Washington, DC', time: '2:00pm' },
        { name: 'Cook Together', address: 'Kalorama, Washington, DC', time: '4:00pm' },
        { name: 'Movie & Wine', address: 'Kalorama, Washington, DC', time: '7:00pm' }
      ],
      'lardente': [
        { name: 'Morning Walk', address: 'Kalorama, Washington, DC', time: '10:30am' },
        { name: 'L\'Ardente Lunch', address: '200 Massachusetts Ave NW, Washington, DC', time: '12:30pm' },
        { name: 'Afternoon at Home', address: 'Kalorama, Washington, DC', time: '3:00pm' }
      ],
      // Christmas Day options
      'morning-run': [
        { name: 'Rock Creek Run', address: 'Rock Creek Park, Washington, DC', time: '9:00am' },
        { name: 'Brunch at Home', address: 'Kalorama, Washington, DC', time: '11:00am' },
        { name: 'Movie Marathon', address: 'Your place', time: '1:00pm' }
      ],
      'lucky-danger': [
        { name: 'Georgetown Waterfront', address: 'Washington Harbour, Georgetown, DC', time: '2:00pm' },
        { name: 'Hot Chocolate', address: 'Georgetown, DC', time: '4:00pm' },
        { name: 'Lucky Danger', address: '1101 4th St SW, Washington, DC', time: '6:30pm' }
      ],
      'monuments': [
        { name: 'National Mall Walk', address: 'National Mall, Washington, DC', time: '11:00am' },
        { name: 'Tiger Fork Lunch', address: '922 N St NW, Washington, DC', time: '1:00pm' },
        { name: 'Evening In', address: 'Kalorama, Washington, DC', time: '4:00pm' }
      ]
    };
    return itineraries[pick] || [];
  };

  const getMapUrl = (pick: string): string => {
    const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

    if (API_KEY) {
      const locations = getItineraryData(pick);
      if (locations.length > 0) {
        const origin = encodeURIComponent(locations[0].address);
        const destination = encodeURIComponent(locations[locations.length - 1].address);
        const waypoints = locations.slice(1, -1).map(loc => encodeURIComponent(loc.address)).join('|');

        let url = `https://www.google.com/maps/embed/v1/directions?key=${API_KEY}&origin=${origin}&destination=${destination}`;
        if (waypoints) url += `&waypoints=${waypoints}`;
        url += '&mode=walking';
        return url;
      }
    }

    const mapUrls: Record<string, string> = {
      'jaleo': 'https://maps.google.com/maps?q=Jaleo+480+7th+St+NW+Washington+DC&output=embed',
      'cozy-cooking': 'https://maps.google.com/maps?q=Whole+Foods+2201+I+St+NW+Washington+DC&output=embed',
      'lardente': 'https://maps.google.com/maps?q=LArdente+200+Massachusetts+Ave+Washington+DC&output=embed',
      'morning-run': 'https://maps.google.com/maps?q=Rock+Creek+Park+Washington+DC&output=embed',
      'lucky-danger': 'https://maps.google.com/maps?q=Lucky+Danger+1101+4th+St+SW+Washington+DC&output=embed',
      'monuments': 'https://maps.google.com/maps?q=National+Mall+Washington+DC&output=embed'
    };
    return mapUrls[pick] || 'https://maps.google.com/maps?q=Washington+DC&output=embed';
  };

  const filteredActivities = useMemo(() => {
    const tolerance = 1;
    return activities.filter(a => Math.abs(a.romanceLevel - romanceLevel) <= tolerance);
  }, [romanceLevel]);

  const filteredConsumables = useMemo(() => {
    const tolerance = 1;
    return consumables.filter(c => Math.abs(c.romanceLevel - romanceLevel) <= tolerance);
  }, [romanceLevel]);

  const generateDate = () => {
    setIsSpinning(true);

    setTimeout(() => {
      const randomActivity = filteredActivities[Math.floor(Math.random() * filteredActivities.length)];
      const randomConsumable = filteredConsumables[Math.floor(Math.random() * filteredConsumables.length)];
      const newDate = {
        activity: randomActivity.name,
        consumable: randomConsumable.name
      };

      setCurrentDate(newDate);
      setHistory(prev => [newDate, ...prev.slice(0, 4)]);
      setIsSpinning(false);
    }, 1500);
  };

  const getRomanceLabel = (level: number) => {
    const labels = ['Active', 'Cozy', 'Festive', 'Romantic', 'Indulgent'];
    return labels[level - 1];
  };

  return (
    <div className="date-gen-container">
      <div className="header">
        <h1>Christmas with Jenn</h1>
        <p className="subtitle">December 24–25, 2025</p>
      </div>

      <div className="romance-slider-container">
        <div className="slider-header">
          <span className="slider-label">Vibe</span>
          <span className="romance-indicator">
            {getRomanceLabel(romanceLevel)}
          </span>
        </div>
        <div className="slider-wrapper">
          <span className="slider-end">Active</span>
          <input
            type="range"
            min="1"
            max="5"
            value={romanceLevel}
            onChange={(e) => setRomanceLevel(parseInt(e.target.value))}
            className="romance-slider"
          />
          <span className="slider-end">Indulgent</span>
        </div>
      </div>

      <div className="spinner-container">
        <div className={`spinner ${isSpinning ? 'spinning' : ''}`}>
          {isSpinning ? (
            <div className="spinning-content">
              <div className="spin-text">Composing your holiday...</div>
            </div>
          ) : currentDate ? (
            <div className="date-result">
              <div className="date-component">
                <span className="label">The Experience</span>
                <span className="value">{currentDate.activity}</span>
              </div>
              <div className="divider">&</div>
              <div className="date-component">
                <span className="label">Paired With</span>
                <span className="value">{currentDate.consumable}</span>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <p>What shall the holiday hold?</p>
            </div>
          )}
        </div>
      </div>

      <button
        className={`spin-button ${isSpinning ? 'disabled' : ''}`}
        onClick={generateDate}
        disabled={isSpinning}
      >
        {isSpinning ? 'Composing...' : 'Discover'}
      </button>

      {history.length > 0 && (
        <div className="history">
          <h3>Previous Suggestions</h3>
          <div className="history-list">
            {history.map((date, index) => (
              <div key={index} className="history-item">
                <span>{date.activity} + {date.consumable}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="lucy-saturday">
        <h3>Curated Itineraries</h3>
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'christmas-eve' ? 'active' : ''}`}
            onClick={() => { setActiveTab('christmas-eve'); setJennsPick(''); }}
          >
            Christmas Eve
          </button>
          <button
            className={`tab ${activeTab === 'christmas-day' ? 'active' : ''}`}
            onClick={() => { setActiveTab('christmas-day'); setJennsPick(''); }}
          >
            Christmas Day
          </button>
        </div>

        {activeTab === 'christmas-eve' && (
          <>
            <div className="weather-forecast">
              <h4>Tuesday, December 24</h4>
              <p>High 57°F / Low 33°F — Partly sunny — Perfect for walking</p>
            </div>
            <p className="lucy-subtitle">Max arrives Tuesday evening. Pick your Christmas Eve adventure:</p>
            <div className="lucy-options">
              <div
                className={`lucy-card ${jennsPick === 'jaleo' ? 'selected' : ''}`}
                onClick={() => setJennsPick('jaleo')}
              >
                <h4>Jaleo Spanish Tapas</h4>
                <div className="time-badge">5:00pm – 10:00pm</div>
                <div className="inkind-badge">InKind</div>
                <p><strong>5:00pm</strong> - Walk through Penn Quarter</p>
                <p className="venue-note">Holiday lights, bustling neighborhood</p>
                <p><strong>7:00pm</strong> - <a href="https://www.jaleo.com/" target="_blank" rel="noopener noreferrer">Jaleo</a></p>
                <p className="menu-note">José Andrés tapas — patatas bravas, jamón, paella, sangria</p>
                <p><strong>9:30pm</strong> - Walk through CityCenterDC lights</p>
                <p className="weather-note">Festive Spanish feast, shareable plates</p>
              </div>

              <div
                className={`lucy-card ${jennsPick === 'cozy-cooking' ? 'selected' : ''}`}
                onClick={() => setJennsPick('cozy-cooking')}
              >
                <h4>Cozy Night In</h4>
                <div className="time-badge">2:00pm – late</div>
                <p><strong>2:00pm</strong> - <a href="https://maps.google.com/?q=Whole+Foods+2201+I+St+NW+Washington+DC" target="_blank" rel="noopener noreferrer">Whole Foods</a></p>
                <p className="venue-note">Pick up ingredients together</p>
                <p><strong>4:00pm</strong> - Cook dinner at your place</p>
                <p className="menu-note">Pasta? Fish? Whatever sounds good</p>
                <p><strong>7:00pm</strong> - Movie, wine, couch with Xiao</p>
                <p className="weather-note">Relaxed after your travels + Jenn's recovery</p>
              </div>

              <div
                className={`lucy-card ${jennsPick === 'lardente' ? 'selected' : ''}`}
                onClick={() => setJennsPick('lardente')}
              >
                <h4>L'Ardente Lunch</h4>
                <div className="time-badge">10:30am – 3:00pm</div>
                <p><strong>10:30am</strong> - Morning walk near Kalorama</p>
                <p className="venue-note">Leisurely start to Christmas Eve</p>
                <p><strong>12:30pm</strong> - <a href="https://resy.com/cities/washington-dc/venues/lardente" target="_blank" rel="noopener noreferrer">L'Ardente</a></p>
                <p className="menu-note">$125/pp Seven Fishes lunch — oysters, scallop gnocchi, Dover sole</p>
                <p><strong>3:00pm</strong> - Afternoon at home, evening open</p>
                <p className="weather-note">Elegant Italian lunch, relaxed evening</p>
              </div>
            </div>
            {jennsPick && (
              <>
                <div className="lucy-confirmation">
                  <p>{jennsPick === 'jaleo' ? 'Spanish tapas with José Andrés — festive and fun.' : jennsPick === 'cozy-cooking' ? 'Sometimes the best dates are the quiet ones.' : 'Elegant Italian lunch, evening free for whatever.'}</p>
                </div>
                <div className="itinerary-map">
                  <h4>Your Christmas Eve</h4>
                  <div className="itinerary-timeline">
                    {getItineraryData(jennsPick).map((location, index) => (
                      <div key={index} className="timeline-item">
                        <div className="timeline-time">{location.time}</div>
                        <div className="timeline-location">{location.name}</div>
                      </div>
                    ))}
                  </div>
                  <iframe
                    title="Itinerary Map"
                    width="100%"
                    height="400"
                    frameBorder="0"
                    style={{ border: 0, borderRadius: '10px', marginTop: '15px' }}
                    src={getMapUrl(jennsPick)}
                    allowFullScreen
                  ></iframe>
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'christmas-day' && (
          <>
            <div className="weather-forecast">
              <h4>Wednesday, December 25</h4>
              <p>High 53°F / Low 29°F — Partly sunny — Mild for Christmas</p>
            </div>
            <p className="lucy-subtitle">A day with nowhere to be. Pick your Christmas:</p>
            <div className="lucy-options">
              <div
                className={`lucy-card ${jennsPick === 'morning-run' ? 'selected' : ''}`}
                onClick={() => setJennsPick('morning-run')}
              >
                <h4>Runner's Christmas</h4>
                <div className="time-badge">9:00am – evening</div>
                <p><strong>9:00am</strong> - <a href="https://maps.google.com/?q=Rock+Creek+Park+Washington+DC" target="_blank" rel="noopener noreferrer">Rock Creek</a> morning run</p>
                <p className="venue-note">Easy pace, beautiful trails, ~5 miles?</p>
                <p><strong>11:00am</strong> - Brunch at home</p>
                <p className="menu-note">Eggs, fresh fruit, good coffee</p>
                <p><strong>1:00pm</strong> - Movie marathon & couch time</p>
                <p className="weather-note">Active morning, lazy afternoon — your style</p>
              </div>

              <div
                className={`lucy-card ${jennsPick === 'lucky-danger' ? 'selected' : ''}`}
                onClick={() => setJennsPick('lucky-danger')}
              >
                <h4>Skating & Lucky Danger</h4>
                <div className="time-badge">2:00pm – 9:00pm</div>
                <div className="inkind-badge">InKind</div>
                <p><strong>2:00pm</strong> - <a href="https://maps.google.com/?q=Washington+Harbour+Ice+Rink+Georgetown" target="_blank" rel="noopener noreferrer">Georgetown Waterfront</a> skating</p>
                <p className="venue-note">Open 1-10:30pm Christmas Day</p>
                <p><strong>4:00pm</strong> - Hot chocolate in Georgetown</p>
                <p><strong>6:30pm</strong> - <a href="https://maps.google.com/?q=Lucky+Danger+Washington+DC" target="_blank" rel="noopener noreferrer">Lucky Danger</a> dinner</p>
                <p className="menu-note">Chinese-American — dan dan noodles, crispy duck, cocktails</p>
                <p className="weather-note">Classic Christmas Day — ice rink + Chinese food</p>
              </div>

              <div
                className={`lucy-card ${jennsPick === 'monuments' ? 'selected' : ''}`}
                onClick={() => setJennsPick('monuments')}
              >
                <h4>Mall Walk & Tiger Fork</h4>
                <div className="time-badge">11:00am – 5:00pm</div>
                <p><strong>11:00am</strong> - <a href="https://maps.google.com/?q=National+Mall+Washington+DC" target="_blank" rel="noopener noreferrer">National Mall</a> walk</p>
                <p className="venue-note">Empty monuments, peaceful DC</p>
                <p><strong>1:00pm</strong> - <a href="https://maps.google.com/?q=Tiger+Fork+Shaw+DC" target="_blank" rel="noopener noreferrer">Tiger Fork</a></p>
                <p className="menu-note">Cantonese BBQ, medicinal cocktails — open for lunch Christmas Day</p>
                <p><strong>4:00pm</strong> - Evening in together</p>
                <p className="weather-note">Quiet city, great food, early evening at home</p>
              </div>
            </div>
            {jennsPick && (
              <>
                <div className="lucy-confirmation">
                  <p>{jennsPick === 'morning-run' ? 'Start the day how you love best.' : jennsPick === 'lucky-danger' ? 'A Christmas tradition worth keeping — skating + Chinese.' : 'The city is yours when everyone else is home.'}</p>
                </div>
                <div className="itinerary-map">
                  <h4>Your Christmas Day</h4>
                  <div className="itinerary-timeline">
                    {getItineraryData(jennsPick).map((location, index) => (
                      <div key={index} className="timeline-item">
                        <div className="timeline-time">{location.time}</div>
                        <div className="timeline-location">{location.name}</div>
                      </div>
                    ))}
                  </div>
                  <iframe
                    title="Itinerary Map"
                    width="100%"
                    height="400"
                    frameBorder="0"
                    style={{ border: 0, borderRadius: '10px', marginTop: '15px' }}
                    src={getMapUrl(jennsPick)}
                    allowFullScreen
                  ></iframe>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="footer">
        <p className="tagline">Fresh off Hawaii, ready for the holidays</p>
        <p className="credits">Built with intention</p>
      </div>
    </div>
  );
};

export default DateGen;
